import { gql } from '@apollo/client'

/**
 * Get session fields
 * @internal
 */
export const sessionFieldsQuery: string = `
  id
  customerUid
  ownerInfo {
    slug
  }
  configuration {
    filters {
      attributes
      promotions {
        active
      }
      categories {
        active
      }
      brands {
        active
      }
    }
    stock {
      enabled
      stockThreshold
      hideOutOfStock
      key
      threshold {
        lowWarning {
          id
          warningMessage
          quantity
          enabled
        }
        criticalLowWarning {
          id
          warningMessage
          quantity
          enabled
        }
      }
    }
    promotions {
      enabled
      maximumActive
      autoAdd
      discountFromStock
      disableFreeGoodsAsDiscount
    }
    i18n {
      language
      currencyFormat
      currencySymbol
      currencyCode
      country
      timezone
    }
    workflow
    splitters {
      order {
        active
        maxProduct
      }
    }
    checkoutRules {
      cartConditionPendingMinAmount {
        isActive
        message
        quantity
      }
      cartConditionPendingMaxAmount {
        quantity
        message
        isActive
      }
      cartConditionPendingMinQty {
        quantity
        message
        isActive
      }
      cartConditionPendingMaxQty {
        quantity
        message
        isActive
      }
      cartConditionPendingUserValidation {
        message
        sku
        attribute
        isActive
      }
      cartWarningCheckReturnables {
        message
        sku
        attribute
        isActive
      }
    }
    # A sibling of checkoutRules, never a member of it: the checkout-rule map
    # is a closed set of keys each of which has a handler, and an unhandled key
    # inside it disables that storefront's rule enforcement outright.
    # This "order" is a new node on configuration, unrelated to the "order"
    # inside splitters and groupers.
    #
    # This selection is deployed end to end as of 2026-09-21. The storefront
    # declares Configuration.order, ConfigOrder { confirmation } and all six
    # leaves in staging and in production, and the cinnamon Release carrying
    # the selection went out against that schema. The merge gate that used to
    # sit beside this selection, and the release gate that replaced it, are
    # both discharged: nothing here is waiting on a rollout any more.
    #
    # Keep the rule those gates enforced, because the next field added here
    # needs it. GraphQL validates a whole document against the deployed schema
    # before executing any of it, so a field the deployed storefront does not
    # declare is a document-level FieldsOnCorrectType failure: the server
    # returns no data at all rather than this one field arriving null. And
    # sessionFieldsQuery is interpolated into three documents (this one and
    # both getInitialValues documents), so what breaks is the entire boot path
    # plus order detail, order creation and order confirmation, on every
    # storefront in the estate. Merging main is itself the staging deploy and
    # publishing a Release is the production one, so the order is: storefront
    # merged, tagged, rolled out to staging, rolled out to production, and only
    # then the cinnamon selection and its Release.
    #
    # Skipping that sequence is what took staging down on 2026-09-07. Contrast
    # configuration.i18n.timezone above, which was safe to select the day it
    # was written: that field already existed on the deployed SDL. Selecting a
    # field is only ever inert when the server declares it.
    #
    # A green suite is no evidence that a rollout landed -- every test touching
    # this is mocked. Verify by sending a document that selects the field under
    # a deliberately bogus storefront name and reading the failure class: a 404
    # means the document validated, a GRAPHQL_VALIDATION_FAILED means the field
    # is undeclared. Do not reach for introspection -- it is disabled on the
    # production user API and enabled on staging, so an introspection probe
    # silently measures staging while appearing to answer for production.
    order {
      confirmation {
        required
        pollingTime
        maxAttempts
        timeout
        successStatuses
        failureStatuses
      }
    }
    sessionTtl
    cart {
      quantityAdjustment
    }
    groupers {
      cart {
        session
        active
        product
      }
      order {
        active
        session
        product
      }
    }
    recommendedProducts {
      prefillCart
      suggestedItems
      frequentlyBoughtTogether
    }
    showRecommendations
    notifications {
      type
      enabled
    }
  }
  checkoutRules {
    ruleType
    value
  }
  workflow {
    name
    channel
    channelUid
    userUid
  }
  banners {
    id
    imagesURL
    customerType
    reference
    type
    isActive
    priority
    startDate
    expirationDate
    userAgent
  }
  customer{
    email
    phoneNumber
    code
    customFields
  }
  startedAt
  updatedAt
  status
  finishedAt
  createdAt
  extraIds {
    key
    value
  }
`

/**
 * Get's the string for `session` query.
 * This query gets all existing items in the current session.
 * @internal
 */
const getSessionQuery = gql`
  query session(
    $storefrontName: String!
    $sessionUid: ID!
  ) {
    session(storefrontName: $storefrontName, id: $sessionUid) {
      ${sessionFieldsQuery}
    }
  }
`

export default getSessionQuery
